import React, { useEffect, useMemo, useState } from "react";

import "./WealthSeriesPortfolioTickers.scss";
import { connect } from "react-redux";
import { portfolioSymbolSetter } from "../../../../appRedux/ducks/portfolioTicker";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import { useParams, useLocation } from "react-router-dom";
import { init_portfolio } from "../../../../appRedux/ducks/wealthPortfolio";
import FallbackTickerIcon from "../../../common/FallbackTickerIcon/FallbackTickerIcon";
import { useAuth } from "../../../../contexts/Auth";
import { multiSparklineDataLoad } from "../../../../appRedux/ducks/multiSparklineData";
import SparkLines from "../../../common/SparkLines";
import SimpleDisplay from "../../../common/Tables/renderers/SimpleDisplay";
import { dxFeedSocketListen } from "../../../../appRedux/ducks/dxFeedSocket";

const WealthSeriesPortfolioTickers = ({
  wealthPortfolios,
  loadingPortfolio,
  setTicker,
  portfolioTicker,
  sparklineData,
  sparklineLoading,
  loadSparklineData,
  setSelectedTickerInfo,
  isViewingPastDate,
  setIsViewingPastDate,
  timeframe = "YTD",
  portfolioData,
  listen,
  loading,
}) => {
  const { portfolio, series } = useParams();
  // Use portfolioData from props if available, otherwise fall back to wealthPortfolios[portfolio]
  const wealthPortfolio =
    portfolioData || wealthPortfolios[portfolio] || init_portfolio;
  // Standalone consumers (e.g. Bitcoin Navigator) pass their own loading flag;
  // the wealthPortfolio duck's loading stays true when it was never dispatched.
  const showSkeleton = loading !== undefined ? loading : loadingPortfolio;
  const tickerData = wealthPortfolio.allocations;
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const {
    isBlurred,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  } = useAuth();
  // Helper function to determine if auto portfolio should be used
  const shouldUseAutoPortfolio = useMemo(() => {
    if (series === "atlas") return hasWealthSeriesAtlasAuto;
    if (series === "titan") return hasWealthSeriesTitanAuto;
    if (series === "everest") return hasWealthSeriesEverestAuto;
    return false;
  }, [
    series,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  ]);
  const ticker = queryParams?.get("ticker");
  const sortedTickerData = useMemo(() => {
    // sort tickerData by quantity in descending order
    const sorted = tickerData.sort(
      (a, b) => b?.ticker_weightage - a?.ticker_weightage,
    );
    return sorted;
  }, [tickerData, ticker]);

  const tickers = useMemo(() => {
    return tickerData.map((item) => item.ticker);
  }, [tickerData]);

  useEffect(() => {
    // Load sparkline data for all tickers with the current timeframe
    if (tickers.length > 0) {
      loadSparklineData(
        [...new Set(tickers)],
        "1",
        false,
        timeframe.toUpperCase(),
      ); // Use cache if available
    }
  }, [tickers, loadSparklineData, timeframe]);

  const handleTickerClick = (ticker) => {
    setTicker(ticker);
  };

  useEffect(() => {
    if (tickers.length) {
      listen([...new Set(tickers)], false, false, "auto");
    }
    return () => listen([], false, true);
  }, [tickers]);
  // Update the change percentage calculation to use the current timeframe instead of only YTD
  const getChangePercent = (ticker, price) => {
    if (!sparklineData[ticker] || sparklineData[ticker].length === 0) {
      return null;
    }

    const firstPrice = Number(sparklineData[ticker][0]);
    const currentPrice = Number(
      price || sparklineData[ticker][sparklineData[ticker].length - 1],
    );

    if (!firstPrice || isNaN(firstPrice) || isNaN(currentPrice)) return null;

    const percentChange = ((currentPrice - firstPrice) / firstPrice) * 100;
    return percentChange.toFixed(2);
  };

  // Sparkline options
  const getSparklineOptions = (performanceRatio) => {
    return {
      colors: [performanceRatio > 0 ? "#5EEECD" : "#F32228"],
      chart: {
        type: "area",
        height: 45,
        sparkline: {
          enabled: true,
        },
        width: 70,
      },
      tooltip: {
        theme: "dark",
        fixed: {
          enabled: false,
        },
        x: {
          show: false,
        },
        y: {
          title: {
            formatter() {
              return "   $";
            },
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 1.5,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.1,
          opacityFrom: 0.7,
          opacityTo: 0,
          stops: [0, 95],
        },
      },
    };
  };

  return (
    <div className="ws-portfolio-tickers">
      <div className="ws-portfolio-tickers__header">
        <div className="ws-portfolio-tickers__header__cell">Ticker</div>
        <div className="ws-portfolio-tickers__header__cell">
          {timeframe.toUpperCase()} Change
        </div>
        <div className="ws-portfolio-tickers__header__cell text-right">
          Price
        </div>
        <div className="ws-portfolio-tickers__header__cell text-right">
          Qty (%)
        </div>
      </div>

      <div className="ws-portfolio-tickers__body ws-scroll-element">
        {(showSkeleton
          ? Array.from({ length: 13 }).fill(null)
          : sortedTickerData
        )?.map((item, index) =>
          showSkeleton ? (
            <div className="ws-portfolio-tickers__body__row" key={index}>
              <div className="ws-portfolio-tickers__body__row__cell ws-ticker-data">
                <BeautifulSkeleton variant="circular" width={24} height={24} />
                <BeautifulSkeleton width={50} height={32} />
              </div>
              <BeautifulSkeleton
                className="ws-portfolio-tickers__body__row__cell text-right"
                width={30}
                height={32}
              />
              <BeautifulSkeleton
                className="ws-portfolio-tickers__body__row__cell text-right"
                width={90}
                height={32}
              />
              <BeautifulSkeleton
                className="ws-portfolio-tickers__body__row__cell text-right"
                width={30}
                height={32}
              />
              <BeautifulSkeleton
                className="ws-portfolio-tickers__body__row__cell text-right"
                width={90}
                height={32}
              />
            </div>
          ) : (
            <div
              key={index}
              className={`ws-portfolio-tickers__body__row ${item.ticker === portfolioTicker ? "active" : ""}`}
              onClick={() => {
                // Get portfolio with correct auto suffix
                const portfolioKey =
                  portfolio + (shouldUseAutoPortfolio ? "_auto" : "");
                const portfolioData = wealthPortfolios[portfolioKey];

                // Find the most recent adjustment for this ticker if available
                let recentAdjustment = null;
                if (
                  portfolioData?.adjustments &&
                  portfolioData.adjustments.length > 0
                ) {
                  // Get the most recent adjustment that includes this ticker
                  for (const adj of portfolioData.adjustments) {
                    if (adj.adjustments && adj.adjustments[item.ticker]) {
                      recentAdjustment = adj.adjustments[item.ticker];
                      break;
                    }
                  }
                }

                // Use rebalance reason and action either from the item or from the most recent adjustment
                const rebalanceReason =
                  item?.ai_rebalance_reason ||
                  recentAdjustment?.ai_rebalance_reason ||
                  null;
                const action = item?.action || recentAdjustment?.action || null;
                if (isViewingPastDate) {
                  setIsViewingPastDate(false);
                }
                setSelectedTickerInfo({
                  ticker: item?.ticker,
                  reason: rebalanceReason,
                  isBuy: action ? action.toLowerCase() === "buy" : null,
                  date: portfolioData?.last_rebalance_created_at,
                });

                if (item?.ticker) {
                  handleTickerClick(item?.ticker);
                }
              }}
            >
              <div
                className={`ws-portfolio-tickers__body__row__cell ws-ticker-data ${isBlurred ? "blurred" : ""}`}
              >
                <FallbackTickerIcon
                  src={`https://assets.tradealgo.com/stock-logos/${item.ticker}.png`}
                  ticker={item.ticker}
                  className="ws-portfolio-ticker-logo"
                />
                {item.ticker}
              </div>
              <div
                className={`ws-portfolio-tickers__body__row__cell ${isBlurred ? "blurred" : ""}`}
              >
                {sparklineLoading[item.ticker] ? (
                  <BeautifulSkeleton width={120} height={32} />
                ) : (
                  <div className="ytd-change-container">
                    {(() => {
                      const changePercent = getChangePercent(
                        item.ticker,
                        item.price,
                      );
                      const isPositive = changePercent >= 0;

                      return (
                        <>
                          <SparkLines
                            style={{ lineHeight: "normal !important" }}
                            series={[
                              {
                                data: [...(sparklineData[item.ticker] || [])],
                              },
                            ]}
                            options={getSparklineOptions(isPositive ? 1 : -1)}
                            type="area"
                            height={32}
                            width={70}
                          />
                          {changePercent !== null && (
                            <div
                              className="ytd-change-percent"
                              style={{
                                color: isPositive ? "#3EB489" : "#FF3B30",
                                marginLeft: "4px",
                                fontWeight: "600",
                                fontSize: "12px",
                              }}
                            >
                              {isPositive ? "+" : ""}
                              {changePercent}%
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="ws-portfolio-tickers__body__row__cell text-right">
                <SimpleDisplay comparator={item.price} ticker={item.ticker} />
              </div>
              <div className="ws-portfolio-tickers__body__row__cell text-right">
                {(item.ticker_weightage * 100).toFixed(2)}%
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
};
const stateToProps = (state) => ({
  wealthPortfolios: state.wealthPortfolio,
  loadingPortfolio: state.wealthPortfolio.loading,
  portfolioTicker: state.portfolioTicker.symbol,
  sparklineData: state.multiSparklineData.sparklineData,
  sparklineLoading: state.multiSparklineData.loading,
});

const dispatchToProps = (dispatch) => ({
  setTicker: (sym) => dispatch(portfolioSymbolSetter(sym)),
  loadSparklineData: (symbols, resolution, forceRefresh, timeframe) =>
    dispatch(
      multiSparklineDataLoad(symbols, resolution, forceRefresh, timeframe),
    ),
  listen: (symbols, options, disconnect, risk) =>
    dispatch(dxFeedSocketListen(symbols, options, disconnect, risk)),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioTickers);
